/**
 * Template van de productpagina (PDP). Het CMS bewaart alleen de blokken;
 * alle productdata komt rechtstreeks uit het CRM.
 */
import { displayVatRate, isVatExempt, minOrder, packSize, productList, productText, stockLevel, unitLabel } from "@/lib/product";

export const PDP_BLOK_TYPES = [
  { type: "gallery", label: "Fotogalerij", kolom: "links" },
  { type: "merk", label: "Merk (klein label)", kolom: "rechts" },
  { type: "titel", label: "Titel (met inhoud)", kolom: "rechts" },
  { type: "korte_omschrijving", label: "Korte omschrijving", kolom: "rechts" },
  { type: "claims", label: "Claims / keurmerken (los)", kolom: "rechts" },
  { type: "koopblok", label: "Koopblok (prijs, voorraad, aantal, knop, betaalregel)", kolom: "rechts" },
  { type: "argumenten", label: "Waarom dit product: USP's + claims in één blok", kolom: "rechts" },
  { type: "usp", label: "USP's (los)", kolom: "rechts" },
  { type: "tabs", label: "Tabs: omschrijving | ingrediënten/materiaal & gebruik | productgegevens", kolom: "links" },
  { type: "past_bij", label: "Maak de routine compleet (gerelateerde producten)", kolom: "onder" },
  { type: "sku", label: "Artikelnummer + voorraadbadge", kolom: "rechts" },
  { type: "voorraad", label: "Voorraadindicator", kolom: "rechts" },
  { type: "prijs", label: "Prijsblok (los)", kolom: "rechts" },
  { type: "aantal", label: "Aantal + bestelknop (los)", kolom: "rechts" },
  { type: "verpakking", label: "Verpakkingsregel", kolom: "rechts" },
  { type: "omschrijving", label: "Omschrijving (los)", kolom: "links" },
  { type: "inci", label: "INCI / ingrediënten (los)", kolom: "links" },
  { type: "velden", label: "Gegevenstabel (CRM-velden)", kolom: "rechts" },
  { type: "tekst", label: "Vrije tekst met CRM-velden", kolom: "rechts" },
  { type: "lijn", label: "Scheidingslijn", kolom: "rechts" },
];

export const STANDAARD_GEGEVENS = ["sku", "ean", "inhoud", "besteleenheid", "btw"];

/** Standaardtemplate: nieuwe opbouw (briefing PDP juni 2026). */
export const DEFAULT_PDP_BLOKKEN = [
  { id: "std-gallery", type: "gallery", kolom: "links", marge_badge: false },
  { id: "std-tabs", type: "tabs", kolom: "links", velden: [...STANDAARD_GEGEVENS, "huidtype", "hardheid", "materiaal", "venster", "iso_maat", "vorm", "aantal_per_verpakking"] },
  { id: "std-merk", type: "merk", kolom: "rechts" },
  { id: "std-titel", type: "titel", kolom: "rechts" },
  { id: "std-korte-omschrijving", type: "korte_omschrijving", kolom: "rechts" },
  { id: "std-koopblok", type: "koopblok", kolom: "rechts" },
  { id: "std-argumenten", type: "argumenten", kolom: "rechts" },
  { id: "std-past-bij", type: "past_bij", kolom: "onder" },
];

/** Categorie-templates per productgroep (zelfde opbouw, eigen productgegevens). */
export const CATEGORIE_TEMPLATES = {
  Gezichtsreinigers: [...STANDAARD_GEGEVENS, "huidtype"],
  Gezichtsverzorging: [...STANDAARD_GEGEVENS, "huidtype"],
  Tandenborstels: [...STANDAARD_GEGEVENS, "hardheid", "materiaal", "venster"],
  "Tandenborstels Basic": [...STANDAARD_GEGEVENS, "hardheid", "materiaal", "venster"],
  "Interdentale mondzorg": [...STANDAARD_GEGEVENS, "iso_maat", "vorm", "aantal_per_verpakking"],
};

/** Template voor dit product: categorie-template als die bestaat, anders standaard. */
export function resolveTemplate(templates, product) {
  const categorie = product?.webshop_categorie?.trim();
  const perCategorie = templates?.per_categorie || {};
  const gekozen = (categorie && perCategorie[categorie]) || templates?.standaard;
  const blokken = gekozen?.blokken;
  const geldig = Array.isArray(blokken) && blokken.length > 0;
  return { blokken: geldig ? blokken : DEFAULT_PDP_BLOKKEN, mobiel: geldig ? gekozen.mobiel || null : null };
}

/** Standaardvolgorde op mobiel: eerst de linkerkolom (media/tekst), dan het koopblok. */
export function mobielBasis(blokken) {
  const kolom = (b) => b.kolom || "rechts";
  return [
    ...blokken.filter((b) => b.type === "gallery"),
    ...blokken.filter((b) => kolom(b) === "rechts"),
    ...blokken.filter((b) => kolom(b) === "links" && b.type !== "gallery"),
    ...blokken.filter((b) => kolom(b) === "onder"),
  ];
}

/** Waarde van een CRM-veld, klaar om te tonen. */
export function veldWaarde(key, product, { account, t, lang } = {}) {
  const jaNee = (waarde) => (waarde === true ? t("pdp.yes") : waarde === false ? t("pdp.no") : null);
  const eenheid = (aantal) => unitLabel(product?.eenheid || t("cart.pieces"), aantal, lang);

  switch (key) {
    case "sku":
      return product?.sku || null;
    case "ean":
      return product?.ean || null;
    case "eenheid":
      return product?.eenheid || null;
    case "inhoud":
      return product?.inhoud || null;
    case "huidtype": {
      const perTaal = productText(product, "huidtype", lang);
      const waarde = perTaal || product?.huidtype;
      if (Array.isArray(waarde)) return waarde.length ? waarde.join(", ") : null;
      return waarde || null;
    }
    case "usp":
    case "claims": {
      const items = productList(product, key, lang);
      return items.length ? items.join(", ") : null;
    }
    case "inci": {
      const waarde = product?.ingredienten ?? product?.inci;
      if (Array.isArray(waarde)) return waarde.length ? waarde.join(", ") : null;
      return waarde || null;
    }
    case "besteleenheid":
      return packSize(product) ? `${packSize(product)} ${eenheid(packSize(product))}` : null;
    case "minimale_afname":
      return minOrder(product) ? `${minOrder(product)} ${eenheid(minOrder(product))}` : null;
    case "btw":
      return isVatExempt(account) ? t("price.vatExempt") : `${displayVatRate(product, account)}%`;
    case "verkoopprijs":
      return product?.verkoopprijs != null ? String(product.verkoopprijs) : null;
    case "consument_adviesprijs":
      return product?.consument_adviesprijs ? String(product.consument_adviesprijs) : null;
    case "webshop_categorie":
      return product?.webshop_categorie || null;
    case "voorraadstatus":
      return t(`stock.${stockLevel(product)}`);
    case "beschikbaar":
      return jaNee(product?.beschikbaar);
    case "land_actief":
      return jaNee(product?.land_actief);
    case "verkoop_nl":
      return jaNee(product?.verkoop_nl);
    case "verkoop_eu":
      return jaNee(product?.verkoop_eu);
    case "verkoop_buiten_eu":
      return jaNee(product?.verkoop_buiten_eu);
    case "is_klantprijs":
      return jaNee(product?.is_klantprijs);
    case "is_landprijs":
      return jaNee(product?.is_landprijs);
    default: {
      const ruw = productText(product, key, lang) || product?.[key];
      if (ruw === true) return t("pdp.yes");
      if (ruw === false) return t("pdp.no");
      if (Array.isArray(ruw)) return ruw.length ? ruw.join(", ") : null;
      return ruw != null && String(ruw).trim() !== "" ? String(ruw) : null;
    }
  }
}

/** Vervangt {{veld}} in vrije tekst door de waarde uit het CRM. */
export function vulPlaceholders(tekst, product, ctx) {
  return String(tekst || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => {
    const waarde = veldWaarde(key.toLowerCase(), product, ctx);
    return waarde == null ? "" : String(waarde);
  });
}
